import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addLeadComment,
  archiveLead,
  restoreLead,
  bulkUpdateLeads,
  convertLead,
  createAction,
  createLead,
  deleteAction,
  deleteLeadComment,
  deleteLeadCommentAttachment,
  getBoard,
  getLead,
  getLeadTimeline,
  getStats,
  listActions,
  listLeadSourcesInUse,
  getLeadFxRates,
  listLeads,
  listOverdueActions,
  updateAction,
  updateLead,
  updateLeadComment,
  type ConvertLeadInput,
  type CreateActionInput,
  type CreateLeadInput,
  type LeadBoardFilters,
  type LeadBulkAction,
  type LeadCommentAttachment,
  type LeadFilters,
  type UpdateActionInput,
  type UpdateLeadInput,
  acceptLeadSuggestion,
  dismissLeadSuggestion,
  generateLeadSuggestions,
  listLeadSuggestions,
  restoreLeadSuggestion,
  type AcceptLeadSuggestionInput,
} from '../lib/leadsApi';

// React Query hooks for the Leads board/list (CLE-191). Mirrors the task hooks.
// `useLeadPipelines` lives in its own module (CLE-193) and is re-exported here
// so callers have a single import surface.
export { useLeadPipelines } from './useLeadPipelines';

// Invalidate every lead-derived query after a mutation so the board, list,
// stats and sidebar badge all refresh together.
function leadKeys(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['leads'] });
  void qc.invalidateQueries({ queryKey: ['lead-board'] });
  void qc.invalidateQueries({ queryKey: ['lead-stats'] });
  void qc.invalidateQueries({ queryKey: ['lead-sources'] });
}

// A move that won a lead (WON rule) also created or linked a client, so the
// client lists and pages refresh too.
function afterWon(qc: ReturnType<typeof useQueryClient>, won: boolean) {
  if (won) void qc.invalidateQueries({ queryKey: ['clients'] });
}

// BNR rates for showing every lead value in the currency picked on the page.
export function useLeadFxRates() {
  return useQuery({
    queryKey: ['lead-fx-rates'],
    queryFn: getLeadFxRates,
    staleTime: 60 * 60_000,
  });
}

// Sources on leads: the typed ones (offered again in the picker) and the
// built-in ones in use (the only ones the filter lists).
export function useLeadSourcesInUse() {
  return useQuery({
    queryKey: ['lead-sources'],
    queryFn: listLeadSourcesInUse,
    staleTime: 60_000,
  });
}

export function useLeads(filters: LeadFilters = {}, enabled = true) {
  return useQuery({
    queryKey: ['leads', filters],
    queryFn: () => listLeads(filters),
    enabled,
  });
}

export function useLead(id: string | undefined) {
  return useQuery({
    queryKey: ['lead', id],
    queryFn: () => getLead(id as string),
    enabled: Boolean(id),
  });
}

export function useLeadBoard(filters: LeadBoardFilters = {}) {
  return useQuery({
    queryKey: ['lead-board', filters],
    queryFn: () => getBoard(filters),
    // Keep the previous columns on screen while a new search runs so the
    // Kanban does not flash empty on every keystroke.
    placeholderData: keepPreviousData,
  });
}

export function useLeadStats(pipelineId?: string) {
  return useQuery({
    queryKey: ['lead-stats', pipelineId ?? null],
    queryFn: () => getStats(pipelineId),
    // Leads are also created outside this tab (the voice/chat assistant), so
    // the sidebar badge refreshes on focus and on a slow poll, not only
    // after the user's own edits.
    refetchOnWindowFocus: true,
    refetchInterval: 60 * 1000,
    refetchIntervalInBackground: false,
  });
}

export function useLeadActions(leadId: string | undefined) {
  return useQuery({
    queryKey: ['lead-actions', leadId],
    queryFn: () => listActions(leadId as string),
    enabled: Boolean(leadId),
  });
}

export function useOverdueActions(assigneeId?: string) {
  return useQuery({
    queryKey: ['lead-actions-overdue', assigneeId ?? null],
    queryFn: () => listOverdueActions(assigneeId),
  });
}

export function useCreateLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateLeadInput) => createLead(input),
    onSuccess: (data) => {
      leadKeys(qc);
      afterWon(qc, Boolean(data.transition));
    },
  });
}

// Generic update keyed by the lead id passed at call time — used by the board
// where any card can move, so a per-id hook would not fit.
export function useUpdateLeadById() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string } & UpdateLeadInput) => {
      const { id, ...rest } = input;
      return updateLead(id, rest);
    },
    onSuccess: (data, vars) => {
      leadKeys(qc);
      void qc.invalidateQueries({ queryKey: ['lead', vars.id] });
      afterWon(qc, Boolean(data.transition));
    },
  });
}

export function useArchiveLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => archiveLead(id),
    onSuccess: () => leadKeys(qc),
  });
}

export function useRestoreLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => restoreLead(id),
    onSuccess: (_data, id) => {
      leadKeys(qc);
      void qc.invalidateQueries({ queryKey: ['lead', id] });
    },
  });
}

export function useConvertLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string } & ConvertLeadInput) => {
      const { id, ...rest } = input;
      return convertLead(id, rest);
    },
    onSuccess: (data, vars) => {
      leadKeys(qc);
      void qc.invalidateQueries({ queryKey: ['lead', vars.id] });
      void qc.invalidateQueries({ queryKey: ['projects'] });
      afterWon(qc, Boolean(data.transition));
    },
  });
}

export function useBulkUpdateLeads() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { ids: string[]; action: LeadBulkAction; value?: unknown }) =>
      bulkUpdateLeads(input.ids, input.action, input.value),
    onSuccess: (data) => {
      leadKeys(qc);
      afterWon(qc, Boolean(data.transitions?.length));
    },
  });
}

export function useCreateLeadAction(leadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateActionInput) => createAction(leadId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lead-actions', leadId] });
      void qc.invalidateQueries({ queryKey: ['lead-actions-overdue'] });
      leadKeys(qc);
    },
  });
}

export function useUpdateLeadAction(leadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { actionId: string } & UpdateActionInput) => {
      const { actionId, ...rest } = input;
      return updateAction(leadId, actionId, rest);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lead-actions', leadId] });
      void qc.invalidateQueries({ queryKey: ['lead-actions-overdue'] });
    },
  });
}

export function useDeleteLeadAction(leadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (actionId: string) => deleteAction(leadId, actionId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lead-actions', leadId] });
      void qc.invalidateQueries({ queryKey: ['lead-actions-overdue'] });
      leadKeys(qc);
    },
  });
}

// --- Timeline & comments (CLE-192) -----------------------------------------

// Refresh the timeline, the lead (for commentCount) and the board badge after
// a comment mutation, so the detail panel and parent list stay in sync.
function invalidateLeadThread(qc: ReturnType<typeof useQueryClient>, leadId: string) {
  void qc.invalidateQueries({ queryKey: ['lead-timeline', leadId] });
  void qc.invalidateQueries({ queryKey: ['lead', leadId] });
  void qc.invalidateQueries({ queryKey: ['leads'] });
  void qc.invalidateQueries({ queryKey: ['lead-board'] });
}

export function useLeadTimeline(leadId: string | undefined) {
  return useQuery({
    queryKey: ['lead-timeline', leadId],
    queryFn: () => getLeadTimeline(leadId as string),
    enabled: Boolean(leadId),
  });
}

export function useAddLeadComment(leadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { content: string; attachments?: LeadCommentAttachment[] }) =>
      addLeadComment(leadId, input.content, input.attachments),
    onSuccess: () => invalidateLeadThread(qc, leadId),
  });
}

export function useUpdateLeadComment(leadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      commentId: string;
      content?: string;
      attachments?: LeadCommentAttachment[] | null;
    }) =>
      updateLeadComment(leadId, input.commentId, {
        content: input.content,
        attachments: input.attachments,
      }),
    onSuccess: () => invalidateLeadThread(qc, leadId),
  });
}

export function useDeleteLeadComment(leadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) => deleteLeadComment(leadId, commentId),
    onSuccess: () => invalidateLeadThread(qc, leadId),
  });
}

export function useDeleteLeadCommentAttachment(leadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { commentId: string; index: number; name: string }) =>
      deleteLeadCommentAttachment(leadId, v.commentId, v.index, v.name),
    onSuccess: () => invalidateLeadThread(qc, leadId),
  });
}

// --- AI action suggestions ---------------------------------------------------------

const LEAD_SUGGESTIONS_KEY = (leadId: string) => ['lead-task-suggestions', leadId];

export function useLeadSuggestions(leadId: string | undefined) {
  return useQuery({
    queryKey: LEAD_SUGGESTIONS_KEY(leadId ?? ''),
    queryFn: () => listLeadSuggestions(leadId as string),
    enabled: Boolean(leadId),
  });
}

function invalidateLeadSuggestions(qc: ReturnType<typeof useQueryClient>, leadId: string) {
  void qc.invalidateQueries({ queryKey: LEAD_SUGGESTIONS_KEY(leadId) });
}

export function useGenerateLeadSuggestions(leadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => generateLeadSuggestions(leadId),
    onSuccess: () => invalidateLeadSuggestions(qc, leadId),
  });
}

export function useAcceptLeadSuggestion(leadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: AcceptLeadSuggestionInput }) =>
      acceptLeadSuggestion(leadId, id, input),
    onSuccess: () => {
      invalidateLeadSuggestions(qc, leadId);
      void qc.invalidateQueries({ queryKey: ['lead-actions', leadId] });
      void qc.invalidateQueries({ queryKey: ['lead-actions-overdue'] });
      leadKeys(qc);
    },
  });
}

export function useDismissLeadSuggestion(leadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => dismissLeadSuggestion(leadId, id),
    onSuccess: () => invalidateLeadSuggestions(qc, leadId),
  });
}

export function useRestoreLeadSuggestion(leadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => restoreLeadSuggestion(leadId, id),
    onSuccess: () => invalidateLeadSuggestions(qc, leadId),
  });
}
